import { computed, ref } from 'vue'
import type { ComputedRef, Ref } from 'vue'
import type { GuidedAnswer, GuidedModeDefinition, GuidedModeStep } from '../types/guided-mode.types'
import { findNextUnansweredStepIndex, isStepAnswered, isStepVisible, stepKey } from '../utils/step-engine'

interface UseGuidedReviewFlowOptions {
  answers: Ref<Record<string, GuidedAnswer>>
  currentIndex: Ref<number>
  definition: ComputedRef<GuidedModeDefinition | null>
  visibleSteps: ComputedRef<GuidedModeStep[]>
}

interface VisibleReviewStep {
  key: string
  step: GuidedModeStep
  topLevelIndex: number
}

export function useGuidedReviewFlow(options: UseGuidedReviewFlowOptions) {
  const { answers, currentIndex, definition, visibleSteps } = options
  const editStepKey = ref<string | null>(null)
  const showFollowUpWarning = ref(false)
  let answersSnapshot: Record<string, GuidedAnswer> | null = null
  let initialVisibleReviewStepKeys = new Set<string>()

  const editing = computed(() => editStepKey.value !== null)
  const currentStepIndex = computed(() => {
    if (!editStepKey.value) return currentIndex.value
    const index = visibleSteps.value.findIndex(step => stepKey(step) === editStepKey.value)
    return index >= 0 ? index : currentIndex.value
  })

  function begin(stepIndex: number): boolean {
    const step = visibleSteps.value[stepIndex]
    if (!step) return false
    currentIndex.value = stepIndex
    editStepKey.value = stepKey(step)
    answersSnapshot = cloneAnswers(answers.value)
    initialVisibleReviewStepKeys = new Set(
      visibleReviewSteps(visibleSteps.value, answers.value).map(item => item.key),
    )
    showFollowUpWarning.value = false
    return true
  }

  function requiresFollowUpConfirmation(): boolean {
    if (!editStepKey.value || !answersSnapshot || showFollowUpWarning.value) return false
    const requiresFollowUp = visibleReviewSteps(visibleSteps.value, answers.value).some(item => (
      item.topLevelIndex > currentStepIndex.value
      && !initialVisibleReviewStepKeys.has(item.key)
      && !isStepAnswered(item.step, definition.value, answers.value)
    ))
    if (requiresFollowUp) showFollowUpWarning.value = true
    return requiresFollowUp
  }

  function cancelChange(): boolean {
    if (answersSnapshot) answers.value = cloneAnswers(answersSnapshot)
    reset()
    return true
  }

  function continueChange() {
    showFollowUpWarning.value = false
    answersSnapshot = null
    initialVisibleReviewStepKeys = new Set()
  }

  function nextUnansweredIndex(): number {
    return findNextUnansweredStepIndex(
      visibleSteps.value,
      definition.value,
      answers.value,
      currentStepIndex.value,
    )
  }

  function moveToStep(index: number) {
    currentIndex.value = index
    if (editStepKey.value && visibleSteps.value[index]) {
      editStepKey.value = stepKey(visibleSteps.value[index])
    }
  }

  function reset() {
    editStepKey.value = null
    answersSnapshot = null
    initialVisibleReviewStepKeys = new Set()
    showFollowUpWarning.value = false
  }

  return {
    editing,
    editStepKey,
    showFollowUpWarning,
    currentStepIndex,
    begin,
    requiresFollowUpConfirmation,
    cancelChange,
    continueChange,
    nextUnansweredIndex,
    moveToStep,
    reset,
  }
}

function visibleReviewSteps(
  steps: GuidedModeStep[],
  answers: Record<string, GuidedAnswer>,
): VisibleReviewStep[] {
  return steps.flatMap((step, topLevelIndex) => {
    const key = stepKey(step)
    return [
      { key, step, topLevelIndex },
      ...visibleNestedReviewSteps(step.steps || [], answers, key, topLevelIndex),
    ]
  })
}

function visibleNestedReviewSteps(
  steps: GuidedModeStep[],
  answers: Record<string, GuidedAnswer>,
  parentKey: string,
  topLevelIndex: number,
): VisibleReviewStep[] {
  return steps
    .filter(step => isStepVisible(step, answers))
    .flatMap(step => {
      const key = `${parentKey}>${stepKey(step)}`
      return [
        { key, step, topLevelIndex },
        ...visibleNestedReviewSteps(step.steps || [], answers, key, topLevelIndex),
      ]
    })
}

function cloneAnswers(source: Record<string, GuidedAnswer>) {
  return Object.fromEntries(Object.entries(source).map(([conceptId, answer]) => (
    [conceptId, Array.isArray(answer) ? [...answer] : answer]
  )))
}
